import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Controller, Control, FieldValues, Path, useWatch } from 'react-hook-form';
import {
  FormControl,
  FormLabel,
  FormHelperText,
  TextField,
  Checkbox,
  Chip,
  Box,
  IconButton,
  Paper,
  Popper,
  ClickAwayListener,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore,
  ChevronRight,
  CheckBox,
  CheckBoxOutlineBlank,
  IndeterminateCheckBox,
  Close,
  ArrowDropDown,
} from '@mui/icons-material';
import { List } from 'react-window';
import { useLocationTree } from './useLocationTree';
import { LocationNode, Location } from './types';
import { isFullySelected, isPartiallySelected } from './locationHelpers';

interface FormLocationSelectorProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  locations: Location[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  helperText?: string;
  required?: boolean;
  error?: string;
}

export const FormLocationSelector = <TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  locations,
  label,
  placeholder = 'Select locations',
  style,
  helperText,
  required = false,
  error,
  disabled = false,
}: FormLocationSelectorProps<TFieldValues>) => {
  const tree = useLocationTree(locations);
  const inputRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Get form value to initialize selection
  const formValue = useWatch({ control, name }) as string[] | undefined;

  // Selection state: node id -> boolean
  const [selection, setSelection] = useState<Record<string, boolean>>({});

  // Flatten tree for lookups
  const flatOptions = useMemo(() => {
    const arr: LocationNode[] = [];
    const traverse = (nodes: LocationNode[]) => {
      nodes.forEach((node) => {
        arr.push(node);
        if (node.children) traverse(node.children);
      });
    };
    traverse(tree);
    return arr;
  }, [tree]);

  // Sync selection with form value
  useEffect(() => {
    // If formValue is empty, clear selection
    if (!formValue || formValue.length === 0) {
      if (Object.keys(selection).length > 0) {
        setSelection({});
      }
      return;
    }

    // Check if selection needs to be synced with formValue
    const formValueSet = new Set(formValue);
    const selectedLeafIds = Object.keys(selection).filter((id) => {
      if (!selection[id]) return false;
      const node = flatOptions.find((n) => n.id === id);
      return node && !node.children?.length;
    });

    // If they don't match, sync from formValue
    if (
      formValueSet.size !== selectedLeafIds.length ||
      !selectedLeafIds.every((id) => formValueSet.has(id))
    ) {
      const newSelection: Record<string, boolean> = {};
      formValue.forEach((id: string) => {
        newSelection[id] = true;
      });
      setSelection(newSelection);
    }
  }, [formValue, selection, flatOptions]);

  // Expanded state - start with all expanded
  const allNodeIds = useMemo(() => {
    const ids: string[] = [];
    const traverse = (nodes: LocationNode[]) => {
      nodes.forEach((node) => {
        ids.push(node.id);
        if (node.children) traverse(node.children);
      });
    };
    traverse(tree);
    return new Set(ids);
  }, [tree]);

  const [expanded, setExpanded] = useState<Set<string>>(allNodeIds);

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const selectNode = (
    node: LocationNode,
    selected: boolean,
    onUpdate?: (leafIds: string[]) => void
  ) => {
    setSelection((currentSelection) => {
      const newSelection = { ...currentSelection };
      const recurse = (n: LocationNode) => {
        newSelection[n.id] = selected;
        n.children?.forEach(recurse);
      };
      recurse(node);

      // Update form value with only leaf city IDs
      if (onUpdate) {
        const leafIds = Object.keys(newSelection).filter((id) => {
          if (!newSelection[id]) return false;
          const node = flatOptions.find((n) => n.id === id);
          return node && !node.children?.length;
        });
        onUpdate(leafIds);
      }

      return newSelection;
    });
  };

  // Chips to show selected items
  const chips = useMemo(() => {
    const result: LocationNode[] = [];

    const traverse = (nodes: LocationNode[]) => {
      nodes.forEach((node) => {
        if (isFullySelected(node, selection)) result.push(node);
        else if (node.children) traverse(node.children);
      });
    };
    traverse(tree);
    return result;
  }, [selection, tree]);

  // Filter tree based on search
  const filteredTree = useMemo(() => {
    if (!searchTerm) return tree;

    const lowerSearch = searchTerm.toLowerCase();
    const matchingIds = new Set<string>();

    // Find all matching nodes and their parents
    flatOptions.forEach((node) => {
      if (node.label.toLowerCase().includes(lowerSearch)) {
        matchingIds.add(node.id);
        // Add all parents
        let current = node;
        while (current.parentId) {
          matchingIds.add(current.parentId);
          const parent = flatOptions.find((n) => n.id === current.parentId);
          if (!parent) break;
          current = parent;
        }
      }
    });

    // Filter tree to only include matching nodes
    const filterNodes = (nodes: LocationNode[]): LocationNode[] => {
      return nodes
        .filter((node) => matchingIds.has(node.id))
        .map((node) => ({
          ...node,
          children: node.children ? filterNodes(node.children) : undefined,
        }));
    };

    return filterNodes(tree);
  }, [tree, searchTerm, flatOptions]);

  // Build flat list of visible nodes for virtualization
  const visibleNodes = useMemo(() => {
    const result: Array<{ node: LocationNode; level: number }> = [];

    const traverse = (nodes: LocationNode[], level: number) => {
      nodes.forEach((node) => {
        result.push({ node, level });
        if (node.children?.length && expanded.has(node.id)) {
          traverse(node.children, level + 1);
        }
      });
    };

    traverse(filteredTree, 0);
    return result;
  }, [filteredTree, expanded]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange }, fieldState }) => {
        const currentError = error || fieldState.error?.message;

        return (
          <FormControl fullWidth error={!!currentError} required={required} style={style}>
            {label && (
              <FormLabel sx={{ mb: '20px' }}>
                {label} {required && <span style={{ color: 'red' }}>*</span>}
              </FormLabel>
            )}

            <ClickAwayListener onClickAway={() => setOpen(false)}>
              <Box>
                <TextField
                  ref={inputRef}
                  placeholder={chips.length === 0 ? placeholder : undefined}
                  value=""
                  onClick={() => !disabled && setOpen(!open)}
                  disabled={disabled}
                  error={!!currentError}
                  fullWidth
                  inputProps={{ style: { width: chips.length > 0 ? 0 : '100%' } }}
                  InputProps={{
                    readOnly: true,
                    startAdornment: chips.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          width: '100%',
                          gap: 0.5,
                          py: 0.5,
                          pl: 0.5,
                          flex: 1,
                          mr: 1,
                        }}
                      >
                        {chips.map((node) => (
                          <Chip
                            key={node.id}
                            label={node.label}
                            size="small"
                            onDelete={
                              disabled
                                ? undefined
                                : (e) => {
                                    e.stopPropagation();
                                    selectNode(node, false, onChange);
                                  }
                            }
                            onClick={(e) => e.stopPropagation()}
                            sx={{ flexShrink: 0 }}
                          />
                        ))}
                      </Box>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {chips.length > 0 && !disabled && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelection({});
                              onChange([]);
                            }}
                            sx={{ mr: 0.5 }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        )}
                        <ArrowDropDown />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      minHeight: '40px',
                      height: 'auto',
                      alignItems: 'flex-start',
                      cursor: disabled ? 'default' : 'pointer',
                    },
                    '& .MuiOutlinedInput-input': {
                      width: '0px',
                    },
                  }}
                />

                <Popper
                  open={open}
                  anchorEl={inputRef.current}
                  placement="bottom-start"
                  style={{ width: inputRef.current?.offsetWidth, zIndex: 1300 }}
                >
                  <Paper elevation={8} sx={{ mt: 0.5 }}>
                    {/* Search field inside dropdown */}
                    <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                      <TextField
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                        fullWidth
                        autoFocus
                      />
                    </Box>

                    {/* Virtualized tree */}
                    <Box sx={{ maxHeight: 400 }}>
                      {visibleNodes.length > 0 ? (
                        <List
                          rowComponent={({ index, style }) => {
                            const { node, level } = visibleNodes[index];
                            const fullySelected = isFullySelected(node, selection);
                            const partiallySelected = isPartiallySelected(node, selection);
                            const hasChildren = Boolean(node.children?.length);
                            const isExpanded = expanded.has(node.id);

                            return (
                              <Box
                                style={style}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  px: 1,
                                  '&:hover': { backgroundColor: 'action.hover' },
                                  cursor: 'pointer',
                                }}
                              >
                                <Box sx={{ width: `${level * 32}px`, flexShrink: 0 }} />

                                {hasChildren ? (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleExpanded(node.id);
                                    }}
                                    disabled={disabled}
                                    sx={{ flexShrink: 0, mr: 0.5 }}
                                  >
                                    {isExpanded ? (
                                      <ExpandMore fontSize="small" />
                                    ) : (
                                      <ChevronRight fontSize="small" />
                                    )}
                                  </IconButton>
                                ) : (
                                  <Box sx={{ width: 32, flexShrink: 0 }} />
                                )}

                                <Checkbox
                                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                                  checkedIcon={<CheckBox fontSize="small" />}
                                  indeterminateIcon={<IndeterminateCheckBox fontSize="small" />}
                                  checked={fullySelected}
                                  indeterminate={partiallySelected}
                                  onChange={() => selectNode(node, !fullySelected, onChange)}
                                  disabled={disabled}
                                  size="small"
                                  sx={{ flexShrink: 0, p: '4px' }}
                                />

                                <Box
                                  sx={{ flex: 1, minWidth: 0 }}
                                  onClick={() => selectNode(node, !fullySelected, onChange)}
                                >
                                  {node.label}
                                </Box>
                              </Box>
                            );
                          }}
                          rowCount={visibleNodes.length}
                          rowHeight={40}
                          rowProps={{}}
                          style={{ height: Math.min(visibleNodes.length * 40, 400), width: '100%' }}
                        />
                      ) : (
                        <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                          No locations found
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Popper>
              </Box>
            </ClickAwayListener>

            {(currentError || helperText) && (
              <FormHelperText>{currentError || helperText}</FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
};

export default FormLocationSelector;
